import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPlus,
  faMinus,
  faTractor,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { BasketContext } from "./BasketContext";
import { useApiUrl } from "./ApiContext";
import axios from "axios";
import maize from "./assets/images/maize.png";
import Swal from "sweetalert2";

import Faith from "./assets/pdfs/maizevarieties/Faith.pdf";
import EsMyrdal from "./assets/pdfs/maizevarieties/Es Myrdal.pdf";
import EsConstance from "./assets/pdfs/maizevarieties/Es Constance.pdf";
import Bonnie from "./assets/pdfs/maizevarieties/Bonnie.pdf";
import EsLegolas from "./assets/pdfs/maizevarieties/Es Legolas.pdf";
import Emeleen from "./assets/pdfs/maizevarieties/Emeleen.pdf";
import Crosbey from "./assets/pdfs/maizevarieties/Crosbey.pdf";
import Micheleen from "./assets/pdfs/maizevarieties/Micheleen.pdf";
import EsMetronom from "./assets/pdfs/maizevarieties/Es Metronom.pdf";
import Jakleen from "./assets/pdfs/maizevarieties/Jakleen.pdf";
import Justice from "./assets/pdfs/maizevarieties/Justice.pdf";

const pdfLinks = {
  Faith,
  "Es Myrdal": EsMyrdal,
  "Es Constance": EsConstance,
  Bonnie,
  "Es Legolas": EsLegolas,
  Emeleen,
  Crosbey,
  Micheleen,
  "Es Metronom": EsMetronom,
  Jakleen,
  Justice,
};

const countyOptions = [
  { label: "Bedfordshire", points: 1 },
  { label: "Berkshire", points: 0 },
  { label: "Buckinghamshire", points: 0 },
  { label: "Cambridgeshire", points: -1 },
  { label: "Central Wales", points: 2 },
  { label: "Cheshire", points: 1 },
  { label: "Cumbria", points: 2 },
  { label: "Derbyshire", points: 2 },
  { label: "Devon/Cornwall (North)", points: 2 },
  { label: "Devon/Cornwall (South)", points: 1 },
  { label: "Dorset", points: 0 },
  { label: "Durham", points: 2 },
  { label: "Essex", points: -1 },
  { label: "Gloucestershire", points: 0 },
  { label: "Greater London", points: 0 },
  { label: "Greater Manchester", points: 2 },
  { label: "Hampshire", points: -1 },
  { label: "Herefordshire", points: 0 },
  { label: "Hertfordshire", points: 0 },
  { label: "Isle of Wight", points: -1 },
  { label: "Kent", points: -1 },
  { label: "Lancashire", points: 1 },
  { label: "Leicestershire", points: 1 },
  { label: "Lincolnshire", points: 1 },
  { label: "Merseyside", points: 1 },
  { label: "Norfolk", points: 0 },
  { label: "Northamptonshire", points: 1 },
  { label: "Northumberland", points: 3 },
  { label: "Nottinghamshire", points: 1 },
  { label: "Oxfordshire", points: 0 },
  { label: "Rutland", points: 1 },
  { label: "Shropshire", points: 0 },
  { label: "Somerset", points: 0 },
  { label: "Staffordshire", points: 1 },
  { label: "Suffolk", points: 0 },
  { label: "Surrey", points: 0 },
  { label: "Sussex", points: -1 },
  { label: "Tyne and Wear", points: 2 },
  { label: "Wales North East", points: 1 },
  { label: "Wales North West", points: 2 },
  { label: "Wales South East", points: 1 },
  { label: "Wales South West", points: 1 },
  { label: "Warwickshire", points: 1 },
  { label: "Wiltshire", points: 0 },
  { label: "Worcestershire", points: 1 },
  { label: "Yorkshire (East Riding)", points: 2 },
  { label: "Yorkshire (North)", points: 2 },
  { label: "Yorkshire (South)", points: 1 },
  { label: "Yorkshire (West)", points: 2 },
];

const productImages = importAll(
  require.context("./assets/images/products", false, /\.(png|jpe?g|webp|svg)$/)
);

function importAll(r) {
  let images = {};
  r.keys().forEach((key) => {
    const filename = key.replace("./", "");
    images[filename] = r(key);
  });
  return images;
}

const stripExt = (s) =>
  s
    ?.replace(/\.[^/.]+$/, "")
    .toLowerCase()
    .trim();

const getMaizeImage = (imageUrl) => {
  const matchedKey = Object.keys(productImages).find(
    (key) => stripExt(key) === stripExt(imageUrl)
  );
  return productImages[matchedKey];
};

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const optionalToInt = (v) => (v === "" ? 0 : toInt(v));

const MaizeSelect = () => {
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const { addToBasket } = useContext(BasketContext);

  const [harvestDate, setHarvestDate] = useState("");
  const [drillingDate, setDrillingDate] = useState("");
  const [altitude, setAltitude] = useState("");
  const [soilQuality, setSoilQuality] = useState("");
  const [poorDrainage, setPoorDrainage] = useState("");
  const [annualRainfall, setAnnualRainfall] = useState("");
  const [fieldGradient, setFieldGradient] = useState("");
  const [soilErosionRisk, setSoilErosionRisk] = useState("");
  const [county, setCounty] = useState("");
  const [northFacing, setNorthFacing] = useState("");
  const [exposedToWind, setExposedToWind] = useState("");

  const [score, setScore] = useState(0);
  const [allFieldsFilled, setAllFieldsFilled] = useState(false);
  const [maizeVarieties, setMaizeVarieties] = useState([]);
  const [filteredVarieties, setFilteredVarieties] = useState([]);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [hasClickedFind, setHasClickedFind] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [isMaizeLoading, setIsMaizeLoading] = useState(true);

  useEffect(() => {
    const fetchMaizeVarieties = async () => {
      setIsMaizeLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/maize`);
        setMaizeVarieties(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching maize data:", error);
        setMaizeVarieties([]);
      } finally {
        setIsMaizeLoading(false);
      }
    };
    fetchMaizeVarieties();
  }, [apiUrl]);

  const showSoilErosionHelp = () => {
    Swal.fire({
      icon: "info",
      title: "Soil Erosion Risk",
      html: `
        <div style="text-align:left; line-height:1.5">
          <p><b>Very low (-1)</b> – Flat/free draining, winter cover crop, and well away from watercourses or gateways.</p>
          <p><b>Low (0)</b> – Slight slope, no erosion history, generally well away from vulnerable areas.</p>
          <p><b>Medium (+1)</b> – Some erosion observed or risk factors present (cultivation/undersown), closer to vulnerable areas.</p>
          <p><b>High (+2)</b> – Erosion observed and close to watercourses/gateways; highest risk scenario.</p>
          <hr/>
          <p style="margin:0"><i>Tip:</i> Choose the option that best matches the field’s worst-case area.</p>
        </div>
      `,
      confirmButtonText: "Close",
    });
  };

  const calculateScore = () => {
    const totalScore =
      toInt(harvestDate) +
      toInt(drillingDate) +
      toInt(altitude) +
      toInt(soilQuality) +
      toInt(poorDrainage) +
      toInt(annualRainfall) +
      toInt(fieldGradient) +
      toInt(soilErosionRisk) +
      toInt(county) +
      optionalToInt(northFacing) +
      optionalToInt(exposedToWind);

    const matchingVarieties = maizeVarieties.filter((variety) => {
      return toInt(variety.maturityGroup) === totalScore;
    });

    setScore(totalScore);
    setFilteredVarieties(matchingVarieties);
  };

  const handleReset = () => {
    setHarvestDate("");
    setDrillingDate("");
    setAltitude("");
    setSoilQuality("");
    setPoorDrainage("");
    setAnnualRainfall("");
    setFieldGradient("");
    setSoilErosionRisk("");
    setCounty("");
    setNorthFacing("");
    setExposedToWind("");
    setScore(0);
    setFilteredVarieties([]);
    setSelectedVariety(null);
    setShowResults(false);
    setAllFieldsFilled(false);
    setHasClickedFind(false);
  };

  useEffect(() => {
    const allSelected =
      harvestDate &&
      drillingDate &&
      altitude &&
      soilQuality &&
      poorDrainage &&
      annualRainfall &&
      fieldGradient &&
      soilErosionRisk &&
      county;

    setAllFieldsFilled(!!allSelected);

    if (allSelected) {
      calculateScore();
    }
  }, [
    harvestDate,
    drillingDate,
    altitude,
    soilQuality,
    poorDrainage,
    annualRainfall,
    fieldGradient,
    soilErosionRisk,
    county,
    northFacing,
    exposedToWind,
    maizeVarieties,
  ]);

  const handleAddToBasket = (product) => {
    addToBasket({
      name: product.varietyName,
      description: product.description,
      quantity: quantities[product.varietyName] || 1,
    });
    setSelectedVariety(null);

    let countdown = 5;

    const timerInterval = setInterval(() => {
      countdown -= 1;
      Swal.update({
        cancelButtonText: `Keep Browsing (${countdown})`,
      });

      if (countdown === 0) {
        clearInterval(timerInterval);
        Swal.close();
      }
    }, 1000);

    Swal.fire({
      icon: "success",
      title: "Added to Tractor",
      text: `${product.varietyName} has been added to your tractor.`,
      showCancelButton: true,
      confirmButtonText: "Go to Tractor",
      cancelButtonText: `Keep Browsing (${countdown})`,
      timer: 5000,
      timerProgressBar: true,
      willClose: () => {
        clearInterval(timerInterval);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/tractor");
      }
    });
  };

  const handleQuantityChange = (e, name) => {
    const value = Math.max(1, parseInt(e.target.value) || 1);
    setQuantities((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (selectedVariety) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [selectedVariety]);

  const hasAnyInput =
    harvestDate ||
    drillingDate ||
    altitude ||
    soilQuality ||
    poorDrainage ||
    annualRainfall ||
    fieldGradient ||
    soilErosionRisk ||
    county ||
    northFacing ||
    exposedToWind;

  const canFindMaturityClass =
    allFieldsFilled &&
    score >= 5 &&
    !isMaizeLoading &&
    filteredVarieties.length > 0;

  return (
    <div className="maizeselect-container">
      <div className="maize-selector">
        <img className="MGALogo" src={maize} alt="Maize Picture Transparent" />

        <h2>Maize Maturity Class Selector</h2>

        <div className="maize-filter-options">
          <div className="maize-filter-group">
            <label>Target Harvest Date</label>
            <select
              value={harvestDate}
              onChange={(e) => setHarvestDate(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Harvest Date</option>
              <option value="3">15th - 22nd September</option>
              <option value="2">23rd - 30th September</option>
              <option value="1">1st - 7th October</option>
              <option value="0">8th - 15th October</option>
              <option value="-1">15th October onwards</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Target Drilling Date</label>
            <select
              value={drillingDate}
              onChange={(e) => setDrillingDate(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Drilling Date</option>
              <option value="0">Before 10th April</option>
              <option value="0">10th - 20th April</option>
              <option value="1">21st - 30th April</option>
              <option value="2">1st - 10th May</option>
              <option value="3">After 10th May</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Field Altitude</label>
            <select
              value={altitude}
              onChange={(e) => setAltitude(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Altitude</option>
              <option value="0">0 - 45m</option>
              <option value="1">46 - 150m</option>
              <option value="2">151 - 300m</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Soil Characteristics</label>
            <select
              value={soilQuality}
              onChange={(e) => setSoilQuality(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Soil Quality</option>
              <option value="0">Light / Fine</option>
              <option value="1">Medium / Good</option>
              <option value="2">Heavy / Cloddy</option>
              <option value="3">V Heavy / V Cloddy</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Poor underlying drainage?</label>
            <select
              value={poorDrainage}
              onChange={(e) => setPoorDrainage(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Yes/No</option>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Annual Rainfall</label>
            <select
              value={annualRainfall}
              onChange={(e) => setAnnualRainfall(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Annual Rainfall</option>
              <option value="0">Low (250mm - 600mm)</option>
              <option value="1">Medium (601mm - 849mm)</option>
              <option value="2">High (850mm or more)</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Field Gradient</label>
            <select
              value={fieldGradient}
              onChange={(e) => setFieldGradient(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Gradient</option>
              <option value="0">&lt;=3⁰ Slight</option>
              <option value="1">4⁰ - 10⁰ Medium</option>
              <option value="2">&gt;=11⁰ Steep</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label className="maize-label-with-help">
              Soil Erosion Risk
              <button
                type="button"
                className="maize-help-button"
                onClick={showSoilErosionHelp}
                aria-label="Soil erosion risk help"
                disabled={showResults}
              >
                <FontAwesomeIcon icon={faCircleQuestion} />
              </button>
            </label>
            <select
              value={soilErosionRisk}
              onChange={(e) => setSoilErosionRisk(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Risk</option>
              <option value="-1">Very low risk</option>
              <option value="0">Low risk</option>
              <option value="1">Medium risk</option>
              <option value="2">High risk</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>County</label>
            <select
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select County/Region</option>
              {countyOptions.map((c) => (
                <option key={c.label} value={c.points}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Field Aspect (Optional)</label>
            <select
              value={northFacing}
              onChange={(e) => setNorthFacing(e.target.value)}
              disabled={showResults}
            >
              <option value="">Not selected</option>
              <option value="0">Not North Facing</option>
              <option value="1">North Facing</option>
            </select>
          </div>

          <div className="maize-filter-group">
            <label>Wind Exposure (Optional)</label>
            <select
              value={exposedToWind}
              onChange={(e) => setExposedToWind(e.target.value)}
              disabled={showResults}
            >
              <option value="">Not selected</option>
              <option value="0">Not Highly Exposed</option>
              <option value="1">Highly Exposed to Wind</option>
            </select>
          </div>

          <div className="maize-filter-actions">
            {allFieldsFilled ? (
              score < 5 ? (
                <div className="varieties-available">Not suitable for maize</div>
              ) : isMaizeLoading ? (
                <div className="varieties-available">Loading maize varieties...</div>
              ) : filteredVarieties.length === 0 ? (
                <div className="varieties-available">Not suitable for maize</div>
              ) : (
                <>
                  <div className="varieties-available">
                    {filteredVarieties.length} Maize Varieties Available
                  </div>
                </>
              )
            ) : (
              <div className="varieties-available">
                {hasAnyInput ? "All required fields must be filled in to proceed." : ""}
              </div>
            )}

            {hasClickedFind && <button onClick={handleReset}>Reset Selections</button>}

            {!hasClickedFind && (
              <button
                onClick={() => {
                  setHasClickedFind(true);
                  setShowResults(true);
                }}
                disabled={!canFindMaturityClass}
              >
                Find Maturity Class
              </button>
            )}
          </div>
        </div>

        {showResults && filteredVarieties.length > 0 && (
          <div className="results">
            {filteredVarieties.map((variety, index) => (
              <div
                key={index}
                className="maize-card"
                onClick={() => setSelectedVariety(variety)}
              >
                <img
                  src={getMaizeImage(variety.imageUrl)}
                  alt={variety.varietyName}
                />

                <h3>{variety.varietyName}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html:
                      variety.description.length > 100
                        ? `${variety.description
                            .substring(0, 100)
                            .replace(/\\/g, "<br />")}...`
                        : variety.description.replace(/\\/g, "<br />"),
                  }}
                ></p>
                <div
                  style={{
                    textAlign: "center",
                    color: "blue",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Click to read more
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedVariety && (
          <div className="maize-results-modal">
            <div className="maize-results-content">
              <button
                className="close-modal"
                onClick={() => setSelectedVariety(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

              <h3>{selectedVariety.varietyName}</h3>

              {pdfLinks[selectedVariety.varietyName] && (
                <a
                  href={pdfLinks[selectedVariety.varietyName]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdf-button"
                >
                  {selectedVariety.varietyName} Guide PDF
                </a>
              )}

              <div className="scrollable-content">
                <p>{selectedVariety.description}</p>

                <table className="variety-details-table">
                  <tbody>
                    <tr>
                      <th>COB Ripeness Score</th>
                      <td>{selectedVariety.cobRipenessScore}</td>
                    </tr>
                    <tr>
                      <th>FAO Estimate</th>
                      <td>{selectedVariety.faoEstimate}</td>
                    </tr>
                    <tr>
                      <th>Growing Area</th>
                      <td>{selectedVariety.growingArea}</td>
                    </tr>
                    <tr>
                      <th>Soil Type Suitability</th>
                      <td>{selectedVariety.soilTypeSuitability}</td>
                    </tr>
                    <tr>
                      <th>Early Vigour</th>
                      <td>{selectedVariety.earlyVigour}</td>
                    </tr>
                    <tr>
                      <th>Recommended Sowing Rate/Acre</th>
                      <td>{selectedVariety.recSowingRate}</td>
                    </tr>
                    <tr>
                      <th>Yield (% of Controls)</th>
                      <td>{selectedVariety.yieldOfControls}</td>
                    </tr>
                    <tr>
                      <th>Starch Content</th>
                      <td>{selectedVariety.starchContent}</td>
                    </tr>
                    <tr>
                      <th>ME Level</th>
                      <td>{selectedVariety.meLevel}</td>
                    </tr>
                    <tr>
                      <th>Standing Power</th>
                      <td>{selectedVariety.standingPower}</td>
                    </tr>
                    <tr>
                      <th>Grain</th>
                      <td>{selectedVariety.grain}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="shop-buttons-on-modal">
                <div className="quantity-controls">
                  <button
                    className="quantity-button"
                    onClick={() =>
                      setQuantities((prev) => ({
                        ...prev,
                        [selectedVariety.varietyName]: Math.max(
                          1,
                          (prev[selectedVariety.varietyName] || 1) - 1
                        ),
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>

                  <input
                    type="number"
                    min="1"
                    value={quantities[selectedVariety.varietyName] || 1}
                    onChange={(e) =>
                      handleQuantityChange(e, selectedVariety.varietyName)
                    }
                    className="quantity-input"
                  />

                  <button
                    className="quantity-button"
                    onClick={() =>
                      setQuantities((prev) => ({
                        ...prev,
                        [selectedVariety.varietyName]:
                          (prev[selectedVariety.varietyName] || 1) + 1,
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>

                <button
                  onClick={() => handleAddToBasket(selectedVariety)}
                  className="add-to-basket-button"
                >
                  Add to <FontAwesomeIcon icon={faTractor} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaizeSelect;
